import type { Point } from "./game/geometry";
import type { NavigationState } from "./game/navigation-types";

export const RADAR_VIEWBOX = { width: 100, height: 70 };
const TAP_SLOP = 10;
interface RadarBounds extends Point {
  width: number;
  height: number;
}
interface ScreenTransform {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}
const finitePoint = (point: Point) =>
  Number.isFinite(point.x) && Number.isFinite(point.y);

/** SVG's actual CTM includes viewBox scale, preserveAspectRatio letterboxing and CSS transforms. */
export function radarDestination(
  client: Point,
  screen: ScreenTransform | null,
  bounds: RadarBounds,
): Point | null {
  if (
    !screen ||
    !finitePoint(client) ||
    !finitePoint(bounds) ||
    !Number.isFinite(bounds.width) ||
    !Number.isFinite(bounds.height) ||
    bounds.width <= 0 ||
    bounds.height <= 0 ||
    !Object.values(screen).every(Number.isFinite)
  )
    return null;
  const determinant = screen.a * screen.d - screen.b * screen.c;
  if (!Number.isFinite(determinant) || determinant === 0) return null;
  const dx = client.x - screen.e;
  const dy = client.y - screen.f;
  const local = {
    x: (screen.d * dx - screen.c * dy) / determinant,
    y: (screen.a * dy - screen.b * dx) / determinant,
  };
  const tolerance = 1e-7;
  if (
    !finitePoint(local) ||
    local.x < -tolerance ||
    local.y < -tolerance ||
    local.x > RADAR_VIEWBOX.width + tolerance ||
    local.y > RADAR_VIEWBOX.height + tolerance
  )
    return null;
  const right = bounds.x + bounds.width;
  const bottom = bounds.y + bounds.height;
  if (
    !Number.isFinite(right) ||
    !Number.isFinite(bottom) ||
    right <= bounds.x ||
    bottom <= bounds.y
  )
    return null;
  const edgeX = Math.max(Number.EPSILON, Math.abs(right) * Number.EPSILON * 4);
  const edgeY = Math.max(Number.EPSILON, Math.abs(bottom) * Number.EPSILON * 4);
  return {
    x: Math.max(
      bounds.x,
      Math.min(
        right - edgeX,
        bounds.x + (bounds.width * local.x) / RADAR_VIEWBOX.width,
      ),
    ),
    y: Math.max(
      bounds.y,
      Math.min(
        bottom - edgeY,
        bounds.y + (bounds.height * local.y) / RADAR_VIEWBOX.height,
      ),
    ),
  };
}

/** Only the map contact is owned here; a joystick finger elsewhere stays independent. */
export class RadarTap {
  private contact: {
    id: number;
    origin: Point;
    destination: Point;
    dragged: boolean;
  } | null = null;
  get pointerId(): number | undefined {
    return this.contact?.id;
  }
  begin(id: number, point: Point, destination: Point | null): boolean {
    if (
      this.contact ||
      !destination ||
      !finitePoint(point) ||
      !finitePoint(destination)
    )
      return false;
    this.contact = {
      id,
      origin: { ...point },
      destination: { ...destination },
      dragged: false,
    };
    return true;
  }
  move(id: number, point: Point): void {
    if (
      this.contact?.id === id &&
      (!finitePoint(point) ||
        Math.hypot(
          point.x - this.contact.origin.x,
          point.y - this.contact.origin.y,
        ) >= TAP_SLOP)
    )
      this.contact.dragged = true;
  }
  end(id: number, point: Point): Point | null {
    if (this.contact?.id !== id) return null;
    this.move(id, point);
    const destination = this.contact.dragged ? null : this.contact.destination;
    this.contact = null;
    return destination;
  }
  cancel(id?: number): void {
    if (id === undefined || this.contact?.id === id) this.contact = null;
  }
}

export function navigationDistance(
  navigation: NavigationState,
  pilot: Point,
): number | null {
  if (
    navigation.status !== "flying" ||
    !finitePoint(pilot) ||
    !finitePoint(navigation.destination)
  )
    return null;
  let previous = pilot;
  let distance = 0;
  for (const point of navigation.path.length
    ? navigation.path
    : [navigation.destination]) {
    if (!finitePoint(point)) return null;
    distance += Math.hypot(point.x - previous.x, point.y - previous.y);
    previous = point;
  }
  return Number.isFinite(distance) ? Math.round(distance) : null;
}
